import type { ArtifactInfo } from './types.js'

/**
 * Workspace-relative path the webapp iframe should load.
 *
 * Webapp artifacts are captured as a directory (the build folder, e.g. "dist"),
 * so `filePath` is the directory, not the entry file. The iframe must point at
 * the actual `index.html` inside it — and the URL must end at that file — so the
 * browser resolves the page's relative asset URLs (./assets/x.js) within the
 * webapp directory rather than dropping it. Legacy single-file webapp artifacts
 * (filePath already points at index.html) are returned unchanged.
 */
export function webappEntryRelPath(artifact: ArtifactInfo): string {
  const filePath = artifact.filePath.replace(/^\//, '')
  if (artifact.isDir) {
    return filePath.replace(/\/$/, '') + '/index.html'
  }
  return filePath
}

/**
 * The URL a chat's web app frame loads. The JWT rides in the path so the
 * page's relative sub-resources (JS/CSS/data) carry it. `?v=` is the stored
 * copy's updatedAt: a re-register rewrites the files under the same names, so
 * without a new URL a frame already on screen never reloads (Matt, 29 Oct
 * 2026). The sub-resources are served no-cache, so they follow the page.
 */
export function webappFrameUrl(artifact: ArtifactInfo, sessionId: string, apiBaseUrl: string, authHeader?: string): string {
  const entry = webappEntryRelPath(artifact)
  const token = authHeader?.replace(/^Bearer\s+/, '')
  const url = token
    ? `${apiBaseUrl}/webapp/${sessionId}/${token}/${entry}`
    : `${apiBaseUrl}/agent/session/${sessionId}/workspace/files/${entry}`
  return artifact.updatedAt ? `${url}?v=${artifact.updatedAt}` : url
}

/**
 * Workspace-relative directory of a webapp artifact — the `dist_path` the
 * publish-to-dashboard endpoint expects. Dir artifacts are the directory;
 * entry-file artifacts (webapp/index.html) resolve to their parent.
 */
export function webappDirRelPath(artifact: ArtifactInfo): string {
  const filePath = artifact.filePath.replace(/^\//, '').replace(/\/$/, '')
  if (artifact.isDir) return filePath
  const i = filePath.lastIndexOf('/')
  return i === -1 ? '' : filePath.slice(0, i)
}

/**
 * Merge DB-fetched artifacts (authoritative statuses + ids) with artifacts the
 * SSE stream registered during the live turn.
 *
 * DB rows win on exact path collisions. SSE-only artifacts are kept so
 * just-registered files show before the DB knows them — but a webapp
 * registration carries the ENTRY file path (webapp/index.html) while the
 * capture stores the DIRECTORY (webapp), so the two never collide by path.
 * Without reconciliation the SSE copy stays status='live' forever and the
 * inline preview spins on "Uploading to cloud storage...". When a captured
 * dir artifact covers an SSE-only path, the SSE artifact adopts the dir's
 * status and id (keeping its entry path, which both message-matching and the
 * webapp iframe URL rely on).
 */
export function mergeDbAndSseArtifacts(db: ArtifactInfo[], sse: ArtifactInfo[]): ArtifactInfo[] {
  const dbPaths = new Set(db.map(a => a.filePath))
  const dirRows = db.filter(a => a.isDir)
  const coveringDir = (path: string) =>
    dirRows.find(d => path.startsWith(d.filePath.replace(/\/$/, '') + '/'))

  // A webapp entry (SSE identity, e.g. webapp/index.html) covered by a captured
  // webapp dir row wins over any DB row at the same exact path: write_file
  // auto-registration historically stored the entry HTML as a 'code' artifact,
  // and letting that twin through makes the UI render source instead of the
  // iframe.
  const webappEntries = sse
    .filter(a => a.source === 'registered' && a.artifactType === 'webapp' && !a.isDir)
    .map(a => ({ a, dir: coveringDir(a.filePath) }))
    .filter((x): x is { a: ArtifactInfo; dir: ArtifactInfo } => x.dir !== undefined && x.dir.artifactType === 'webapp')
    .map(({ a, dir }) => ({ ...a, status: dir.status, id: dir.id ?? a.id, updatedAt: dir.updatedAt ?? a.updatedAt }))
  const entryPaths = new Set(webappEntries.map(a => a.filePath))

  const dbKept = db.filter(a => !entryPaths.has(a.filePath))
  const sseOnly = sse
    .filter(a => a.source === 'registered' && !dbPaths.has(a.filePath) && !entryPaths.has(a.filePath))
    .map(a => {
      if (a.status !== 'live') return a
      const dir = coveringDir(a.filePath)
      return dir ? { ...a, status: dir.status, id: dir.id ?? a.id } : a
    })
  return [...dbKept, ...webappEntries, ...sseOnly]
}

export type ArtifactTypeFilter = 'all' | 'images' | 'code' | 'data' | 'reports'
export type ArtifactStatusFilter = 'all' | 'available' | 'lost'

export function filterArtifactsByType(artifacts: ArtifactInfo[], filter: ArtifactTypeFilter): ArtifactInfo[] {
  if (filter === 'all') return artifacts
  switch (filter) {
    case 'images':
      return artifacts.filter(a => a.artifactType === 'image' || a.artifactType === 'chart')
    case 'code':
      return artifacts.filter(a => a.artifactType === 'code')
    case 'data':
      return artifacts.filter(a => a.artifactType === 'csv')
    case 'reports':
      return artifacts.filter(a => a.artifactType === 'report')
    default:
      return artifacts
  }
}

export function filterArtifactsByStatus(artifacts: ArtifactInfo[], filter: ArtifactStatusFilter): ArtifactInfo[] {
  if (filter === 'all') return artifacts
  if (filter === 'available') return artifacts.filter(a => a.status !== 'lost')
  if (filter === 'lost') return artifacts.filter(a => a.status === 'lost')
  return artifacts
}

export function filterArtifactsBySearch(artifacts: ArtifactInfo[], search: string): ArtifactInfo[] {
  if (!search.trim()) return artifacts
  const term = search.toLowerCase()
  return artifacts.filter(a =>
    a.fileName.toLowerCase().includes(term) ||
    a.label.toLowerCase().includes(term)
  )
}

const LANGUAGE_MAP: Record<string, string> = {
  py: 'Python',
  js: 'JavaScript',
  jsx: 'JavaScript',
  ts: 'TypeScript',
  tsx: 'TypeScript',
  sql: 'SQL',
  sh: 'Shell',
  bash: 'Shell',
  r: 'R',
  rb: 'Ruby',
  go: 'Go',
  rs: 'Rust',
  java: 'Java',
  cs: 'C#',
  css: 'CSS',
  html: 'HTML',
  json: 'JSON',
  yaml: 'YAML',
  yml: 'YAML',
  xml: 'XML',
  md: 'Markdown',
}

export function getLanguageFromFilename(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return LANGUAGE_MAP[ext] || 'Code'
}

export interface CSVPreviewResult {
  columns: string[]
  rows: string[][]
  totalRows: number
}

export function parseCSVPreview(content: string, maxRows: number): CSVPreviewResult {
  if (!content.trim()) {
    return { columns: [], rows: [], totalRows: 0 }
  }

  const lines = parseCSVLines(content)
  if (lines.length === 0) {
    return { columns: [], rows: [], totalRows: 0 }
  }

  const columns = lines[0]
  const dataLines = lines.slice(1)
  const rows = dataLines.slice(0, maxRows)

  return {
    columns,
    rows,
    totalRows: dataLines.length,
  }
}

function parseCSVLines(content: string): string[][] {
  const results: string[][] = []
  let current = ''
  let inQuotes = false
  let row: string[] = []

  for (let i = 0; i < content.length; i++) {
    const ch = content[i]

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < content.length && content[i + 1] === '"') {
          current += '"'
          i++ // skip escaped quote
        } else {
          inQuotes = false
        }
      } else {
        current += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        row.push(current)
        current = ''
      } else if (ch === '\n') {
        row.push(current)
        current = ''
        if (row.length > 0) results.push(row)
        row = []
      } else if (ch === '\r') {
        // skip
      } else {
        current += ch
      }
    }
  }

  // push last field/row
  row.push(current)
  if (row.some(cell => cell.length > 0)) {
    results.push(row)
  }

  return results
}
